import React, { useState, useEffect } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useQuery } from 'react-query';
import { apiService } from '../../../core/services/apiService';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import styles from './SchedulerSetting.module.scss';
import CreateBroadcastGroup from './BroadcastSetting/CreateBroadcastGroup';
import BroadcastGroupsList from './BroadcastSetting/BroadcastGroupsList';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';

interface BroadcastGroup {
	id: number;
	groupName: string;
	participants?: any[];
	securityRoles?: any[];
	workplace?: string | null;
}

type ViewMode = 'list' | 'createBroadcastGroup' | 'editBroadcastGroup';

const BoradcastSetting = () => {
	const { workplaceId } = useWorkplacesContext();
	const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } = usePermissionCheck();
	const canViewBroadcast = hasViewPermission('Broadcast');
	const canEditBroadcast = hasEditPermission('Broadcast');
	const canDeleteBroadcast = hasDeletePermission('Broadcast');
	const canExecuteBroadcast = hasExecutePermission('Broadcast');
	const [viewMode, setViewMode] = useState<ViewMode>('list');
	const [editingGroup, setEditingGroup] = useState<BroadcastGroup | null>(null);

	// Fetch broadcast groups
	const { data: broadcastGroupsData, refetch: refetchGroups } = useQuery<{ member: BroadcastGroup[] }>(
		['broadcastGroups', workplaceId],
		async () => {
			const response = apiService.responseHandler(
				await apiService.get('/broadcast_groups', {
					params: {
						workplace: workplaceId || 1,
						page: 1,
						itemsPerPage: 100,
						pagination: false,
					},
				})
			);
			// Handle response with member array
			if (response?.member && Array.isArray(response.member)) {
				return { member: response.member };
			}
			// Handle direct array response
			if (Array.isArray(response)) {
				return { member: response };
			}
			return { member: [] };
		},
		{
			enabled: !!workplaceId && viewMode === 'list' && canViewBroadcast,
		}
	);

	const broadcastGroups = broadcastGroupsData?.member || [];

	const handleBackClick = () => {
		setViewMode('list');
		setEditingGroup(null);
		refetchGroups();
	};

	const handleBroadcastGroupCreated = () => {
		refetchGroups();
	};

	// Fetch full group details when editing
	const { data: fullGroupData } = useQuery<BroadcastGroup>(
		['broadcastGroup', editingGroup?.id],
		async () => {
			if (!editingGroup?.id) return null;
			const response = apiService.responseHandler(
				await apiService.get(`/broadcast_groups/${editingGroup.id}`)
			);
			return response;
		},
		{
			enabled: viewMode === 'editBroadcastGroup' && !!editingGroup?.id,
		}
	);

	if (!canViewBroadcast) {
		return (
			<div className={styles.schedulerSettings}>
				<Card className="card">
					<div style={{ padding: '20px', textAlign: 'center' }}>
						You do not have permission to view broadcast settings.
					</div>
				</Card>
			</div>
		);
	}

	if (viewMode === 'createBroadcastGroup') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateBroadcastGroup 
					backButtonClick={handleBackClick} 
					onBroadcastGroupCreated={handleBroadcastGroupCreated}
				/>
			</div>
		);
	}

	if (viewMode === 'editBroadcastGroup' && fullGroupData) {
		return (
			<div className={styles.schedulerSettings}>
				<CreateBroadcastGroup 
					backButtonClick={handleBackClick} 
					onBroadcastGroupCreated={handleBroadcastGroupCreated}
					editingGroup={fullGroupData}
				/>
			</div>
		);
	}

	return (
		<div className={styles.schedulerSettings}>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Broadcast Groups</h2>
					<Button
						className={styles.addButton}
						onClick={() => setViewMode('createBroadcastGroup')}
						disabled={!canExecuteBroadcast}
						title={canExecuteBroadcast ? 'Add Broadcast Group' : 'You do not have permission to add'}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					<BroadcastGroupsList
						broadcastGroups={broadcastGroups}
						onBroadcastGroupCreated={handleBroadcastGroupCreated}
						onEditClick={(group) => {
							if (!canEditBroadcast) {
								return;
							}
							setEditingGroup(group);
							setViewMode('editBroadcastGroup');
						}}
					/>
				</div>
			</Card>
		</div>
	);
};

export default BoradcastSetting;